import clsx from 'clsx';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { ShadowRoot } from '@/src/components/common/Elements/ShadowRoot/ShadowRoot';
import RecipientListPopper from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/components/EmailThreadViewer/EmailEntries/RecipientListPopper/RecipientListPopper';
import { MESSAGE_HISTORY_TW_SUPPORT_STYLE } from '@/src/components/project/Inbox2/Config/inbox.config';
import AttachmentsCount from '@/src/components/project/Inbox2/Elements/Buttons/AttachmentsCount/AttachmentsCount';
import { EllipsisThreadButton } from '@/src/components/project/Inbox2/Elements/Buttons/EllipsisThreadButton/EllipsisThreadButton';
import EntryCard from '@/src/components/project/Inbox2/Elements/EntryCard/EntryCard';
import { parseInlineImages } from '@/src/components/project/Inbox2/Helper';
import { getOpenInboundAttachmentsCount } from '@/src/components/project/Inbox2/models/EmailEntry';
import {
  EmailEntry,
  EmailEntryFiles,
  EmailEntryListItem,
  EmailEntryType,
  EmailFileEntry,
} from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';
import { DEFAULT_DATE_FORMAT } from '@/src/config';
import { useToggle } from '@/src/hooks/useToggle';
import { Authorization } from '@/src/lib/authorization/authorization';
import { RootState } from '@/src/reducers/types';
import { parseAndSanitizeHtml } from '@/src/util/html';

import Attachments from './Attachments/Attachments';
import {
  getEmailEntryToUpdateStatus,
  getEmailTypeDisplay,
  getUserDisplay,
} from './EmailEntryExpanded';
import ToggleCreateReply from './ToggleCreateReply/ToggleCreateReply';

import { useEmailThreadEntryViewer } from '../../../../store/emailThreadEntryViewer.store';
import ActiveFilesBadge from '../../../Attachments/ActiveFilesBadge/ActiveFilesBadge';
import UpdateEmailStatus from '../../../UpdateEmailStatus/UpdateEmailStatus';

const DATE_FORMAT = `${DEFAULT_DATE_FORMAT} h:mm A`;
const EMAIL_THREAD_HEIGHT_LIMIT = 800;

export const checkOverflow = (height) => {
  return height >= EMAIL_THREAD_HEIGHT_LIMIT;
};

type EmailEntryExpandedDetailsProps = {
  emailEntry: EmailEntry;
  emailEntryListItem: EmailEntryListItem;
  isLastItem?: boolean;
  onClick: (emailEntry: EmailEntry) => void;
};

const EmailEntryExpandedDetails = ({
  emailEntry,
  emailEntryListItem,
  onClick,
  isLastItem = false,
}: EmailEntryExpandedDetailsProps) => {
  const searchTerm = useSelector((state: RootState) => state.filters.searchTerm);
  const { isTrash } = useInboxSearchParams();
  const shouldScrollIntoView = isLastItem === true;
  const {
    selectEmailEntry,
    selectedEmailEntryListItem,
    selectEmailFileEntry,
    setSelectedEmailEntry,
  } = useEmailThreadEntryViewer();

  const isFirstRender = useRef(true);
  const emailBodyRef = useRef(null);
  const emailContentRef = useRef(null);
  const [height, setHeight] = useState(null);
  const [showEllipsisContent, toggleShowEllipsisContent] = useToggle(false);

  useEffect(() => {
    if (!emailBodyRef.current || !emailContentRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (emailContentRef.current) {
        setHeight(emailContentRef.current.offsetHeight);
      }
    });

    resizeObserver.observe(emailContentRef.current);

    if (shouldScrollIntoView && isFirstRender.current) {
      emailBodyRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    isFirstRender.current = false;

    return () => {
      resizeObserver.disconnect();
    };
  }, [shouldScrollIntoView]);

  const handleSelect = useCallback(() => {
    selectEmailEntry({
      selectedEmailEntry: emailEntry,
      selectedEmailEntryListItem: emailEntryListItem,
    });
  }, [emailEntry, emailEntryListItem, selectEmailEntry]);

  const handleClick = useCallback(() => {
    handleSelect();
    onClick(emailEntry);
  }, [emailEntry, handleSelect, onClick]);

  const handleSelectEmailFileEntry = useCallback(
    (emailFileEntry: EmailFileEntry) => {
      selectEmailFileEntry(emailEntry, emailFileEntry);
    },
    [emailEntry, selectEmailFileEntry]
  );

  const dateReceived = moment(emailEntryListItem.dateAdded).format(DATE_FORMAT);
  const dateReceivedFromNow = moment(emailEntryListItem.dateAdded).fromNow();
  const isOverflow = checkOverflow(height);
  const parsedMessage = parseAndSanitizeHtml(
    parseInlineImages(emailEntry.html),
    { FORBID_TAGS: [] },
    searchTerm
  );
  const userDisplay = getUserDisplay(emailEntryListItem.from);
  const isCurrentActive = selectedEmailEntryListItem?.id === emailEntryListItem.id;
  const emailEntryToUpdateStatus = getEmailEntryToUpdateStatus(emailEntryListItem);
  const openInboundAttachmentsCount = getOpenInboundAttachmentsCount(emailEntryListItem.files.list);
  const emailTypeDisplay = getEmailTypeDisplay(emailEntryListItem.emailType);
  const isSentEmailType = emailEntryListItem.emailType === EmailEntryType.SENT;

  useEffect(() => {
    if (!emailEntry) {
      return;
    }

    if (isCurrentActive) {
      setSelectedEmailEntry(emailEntry);
    }
  }, [isCurrentActive, setSelectedEmailEntry, emailEntry]);

  return (
    <React.Fragment>
      <EntryCard
        className={clsx(
          'container--emailExpanded',
          'tw-rounded-[5px]',
          'tw-p-[10px] tw-mb-[10px]',
          'hover:tw-shadow-[1px_2px_4px_0px_rgba(80,129,255,0.20)]',
          emailEntryListItem.isRead && '!tw-bg-cfo-grey-100'
          // isCurrentActive && '!tw-bg-cfo-blue-200'
        )}
        isActive={isCurrentActive}
      >
        {/* Email Header ( Avatar, Name, Date Received) */}
        <div ref={emailBodyRef}>
          <div
            className={clsx(
              'tw-flex tw-items-center tw-justify-between',
              'tw-pb-[10px]',
              'tw-cursor-pointer'
            )}
            onClick={handleClick}
            onKeyDown={handleClick}
            tabIndex={0}
            role="button"
          >
            <div className={clsx('tw-flex tw-items-center')}>
              {userDisplay}
              {/* <p className={clsx('tw-font-brandonTextMedium tw-text-cfo-black', 'tw-ml-[10px]')}>{emailEntryItem.emailFrom.email}</p> */}
            </div>

            <div className={clsx('tw-flex tw-items-center tw-gap-1')}>
              {isSentEmailType && (
                <Icon
                  icon={ICONS.outbound({
                    height: '24',
                    width: '24',
                    className: 'tw-text-cfo-grey-600',
                  })}
                />
              )}
              <p className={clsx('tw-p-0', 'tw-text-cfo-black tw-transform')}>
                {emailTypeDisplay} {dateReceived} ({dateReceivedFromNow})
              </p>
            </div>
          </div>

          <div className="tw-cursor-default">
            {/* Email Status */}
            <div className={clsx('tw-flex tw-items-center tw-justify-between', 'tw-ml-[40px]')}>
              <RecipientListPopper
                emailEntry={{
                  from: emailEntryListItem.from,
                  to: emailEntryListItem.to,
                  cc: emailEntryListItem.cc,
                }}
              />

              {!emailEntryListItem.isDeleted && emailEntryToUpdateStatus && (
                <UpdateEmailStatus emailEntry={emailEntryToUpdateStatus} />
              )}
            </div>

            {/* Email Summary */}
            <div
              ref={emailContentRef}
              className={clsx(
                'container--emailBody',
                'tw-pl-[40px] tw-py-[15px]',
                'tw-break-words'
              )}
            >
              <div
                className={clsx(
                  'container--parsedMessage',
                  'tw-text-cfo-black',
                  'tw-m-0',
                  showEllipsisContent ? 'tw-max-h-full' : 'tw-max-h-[800px] tw-overflow-hidden'
                )}
                onClick={handleSelect}
                onKeyDown={handleSelect}
                tabIndex={0}
                role="button"
              >
                <ShadowRoot
                  htmlView={parsedMessage}
                  styleTagContent={MESSAGE_HISTORY_TW_SUPPORT_STYLE}
                />
              </div>

              {isOverflow && (
                <EllipsisThreadButton
                  className={clsx('tw-border-solid tw-border-[1px] tw-border-cfo-grey-500')}
                  onClick={toggleShowEllipsisContent}
                />
              )}
            </div>

            {/* Email Attachments Count */}
            <div
              className={clsx(
                'container--emailAttachments',
                'tw-w-full',
                'tw-border-solid tw-border-t-[1px] tw-border-t-cfo-grey-500',
                'tw-p-[10px] tw-mt-[10px]',
                emailEntryListItem.files.list.length <= 0 && 'tw-hidden'
              )}
            >
              <div className={clsx('tw-w-full', 'tw-flex tw-items-center')}>
                {emailEntryListItem.files.list.length > 0 && (
                  <AttachmentsCount
                    count={emailEntryListItem.files.total}
                    className={clsx(
                      'tw-px-[10px] tw-py-[5px]',
                      'tw-rounded-[34px] ',
                      'tw-bg-cfo-grey-100'
                    )}
                  />
                )}
                {!isTrash && !isSentEmailType && (
                  <ActiveFilesBadge count={openInboundAttachmentsCount} />
                )}
              </div>

              {emailEntryListItem.files.list.length > 0 && (
                <Attachments
                  className={clsx('tw-mt-[10px]')}
                  inboxEntryId={emailEntryListItem.inboxEntryId}
                  preloadedEmailFileEntries={emailEntryListItem.files as EmailEntryFiles}
                  onSetEmailFileEntry={handleSelectEmailFileEntry}
                  isSentEmailType={isSentEmailType}
                />
              )}
            </div>
          </div>

          {emailEntry && (
            <Authorization policyCheck={emailEntry.permissions.createReply}>
              <ToggleCreateReply emailEntry={emailEntry} emailEntryListItem={emailEntryListItem} />
            </Authorization>
          )}
        </div>
      </EntryCard>
    </React.Fragment>
  );
};

export default EmailEntryExpandedDetails;
